import type {
  DatumAbstraction,
  datumUsageTypeToDatumAbstractionType,
  VerticalDatumAnchor,
  ResolveAbsVerticalDatumProps,
  ResolveRelVerticalDatumProps,
  ResolveAbsStackedLevelDatumProps,
  ResolveRelStackedLevelDatumProps,
} from "building-types/datums/datumAbstractionTypes";
import {
  isResolveFoundationVerticalDatumProps,
  isResolveLevelVerticalDatumProps,
  isResolveWallVerticalDatumProps,
} from "building-types/datums/datumAbstractionTypes";
import {
  AbsoluteVerticalDatumAbstractionBase,
  LegacyWallVerticalDatumAbstractionBase,
  LegacyFoundationVerticalDatumAbstractionBase,
  LegacyWallHorizontalDatumAbstractionBase,
  WallPlaneHorizontalDatumAbstractionBase,
  LegacyLevelDatumAbstractionBase,
  LegacyFoundationLateralDatumAbstractionBase,
  StackedLevelDatumAbstractionBase,
} from "building-types/datums/datumAbstractionTypes";
import type {
  LegacyFoundationLateralDatumEntity,
  LegacyLevelDatumEntity,
  StackedLevelDatumEntity,
} from "building-types/datums/datumEntityTypes";
import {
  DatumDirection,
  type AbsoluteVerticalDatumEntity,
  type LegacyFoundationVerticalDatumEntity,
  type LegacyWallVerticalDatumEntity,
  type WallHorizontalDatumEntity,
  type WallPlaneHorizontalDatumEntity,
} from "building-types/datums/datumEntityTypes";
import type { WallAssemblyVerticalDatum, FacadeVerticalDatum } from "building-types/datums/datumsTypes";
import type { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import {
  WallVerticalDatumAnchor,
  FoundationDatumVerticalAnchor,
  VerticalJustification,
  HorizontalJustification,
  LevelDatumAnchor,
} from "building-types/datums/datumsTypesEnum";
import type { DatumUsage, DatumUsageType } from "building-types/datums/datumUsageTypes";
import {
  DatumVerticalUsageType,
  DatumHorizontalUsageType,
  DatumLateralUsageType,
  datumUsageTypeToDatumDirectionType,
} from "building-types/datums/datumUsageTypes";
import type { FoundationPath } from "building-types/foundations/foundationTypes";
import type { Level, LevelAttributes } from "building-types/levels/levelTypes";
import type { WallSideDescriptor } from "building-types/walls/wallDesciptorTypes";
import type { WallLayerType } from "building-types/walls/wallEnums";
import type { Table } from "common/data/table";
import type { WorldInches, LevelInches, NominalNumber } from "common/types/nominalNumbersTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import {
  levelInchesNew,
  levelInchesToWorld,
  nominalNumberNew,
  worldInchesNew,
  worldInchesToLevel,
} from "common/utils/nominalNumbers";
import { wallLateralDatumAnchorToWallLayer } from "doc/elements/wallLateralDatumAnchorToWallLayer";
import { resolveInstanceScopedEntity } from "doc/entity/componentInstanceEntityResolve";

/**
 * DatumAbstraction implementation for absolute vertical datums
 */
export class AbsoluteVerticalDatumAbstraction extends AbsoluteVerticalDatumAbstractionBase {
  constructor(datum: AbsoluteVerticalDatumEntity) {
    super(datum);
  }

  resolveVerticalDatum({ extraOffset = 0 }: ResolveAbsVerticalDatumProps): WorldInches {
    return worldInchesNew((this.datum.offset ?? 0) + extraOffset);
  }

  resolveVerticalDatumRelativeToLevel(props: ResolveRelVerticalDatumProps): LevelInches {
    return worldInchesToLevel(this.resolveVerticalDatum(props), props.level.floorElevation);
  }

  isValidForUsage<T extends DatumUsageType>(usageType: T): this is (typeof datumUsageTypeToDatumAbstractionType)[T] {
    return datumUsageTypeToDatumDirectionType[usageType] === DatumDirection.VERTICAL;
  }

  hasCompatibleAnchor(_anchor: VerticalDatumAnchor): boolean {
    return true;
  }

  isBaseVerticalDatumType(_isBottomDatum: boolean, _extraOffset?: number): boolean {
    return false;
  }

  isRoofDatum(): boolean {
    return true;
  }

  isValidForLevel(_levelId: string | undefined): boolean {
    return true;
  }
}

/**
 * DatumAbstraction implementation for legacy wall vertical datums
 */
export class LegacyWallVerticalDatumAbstraction extends LegacyWallVerticalDatumAbstractionBase {
  constructor(datum: LegacyWallVerticalDatumEntity) {
    super(datum);
  }

  isValidForUsage<T extends DatumUsageType>(usageType: T): this is (typeof datumUsageTypeToDatumAbstractionType)[T] {
    return usageType === DatumVerticalUsageType.WALL_VERTICAL;
  }

  hasCompatibleAnchor(anchor: WallVerticalDatumAnchor): boolean {
    return this.datum.anchor === anchor;
  }

  isBaseVerticalDatumType(isBottomDatum: boolean, extraOffset: number = 0): boolean {
    // Some finishes use an erroneous positioning method for calculating top and bottom offsets for finishes
    // from wall faces, where the wall face BASE_WALL can be located at an unpredictable location.
    // This functionality should be deprecated to use a more consistent positioning methodology
    // where BASE_WALL always represents the same datum regardless of the wall geometry. Until then,
    // we need to maintain existing functionality.

    return (
      // base wall bottom
      (isBottomDatum &&
        this.datum.anchor === WallVerticalDatumAnchor.BASE_WALL &&
        this.datum.justification === VerticalJustification.BOTTOM_UP &&
        this.datum.offset === 0 &&
        extraOffset === 0) ||
      // base wall top
      (!isBottomDatum &&
        this.datum.anchor === WallVerticalDatumAnchor.BASE_WALL &&
        this.datum.justification === VerticalJustification.TOP_DOWN &&
        this.datum.offset === 0 &&
        extraOffset === 0)
    );
  }

  isRoofDatum(): boolean {
    return this.datum.anchor === WallVerticalDatumAnchor.GABLE_END;
  }

  resolveVerticalDatum(props: ResolveAbsVerticalDatumProps): WorldInches {
    if (!isResolveWallVerticalDatumProps(props)) {
      throw new Error("Invalid props type for LegacyWallVerticalDatumAbstraction");
    }

    return levelInchesToWorld(this.resolveVerticalDatumRelativeToLevel(props), props.level.floorElevation);
  }

  resolveVerticalDatumRelativeToLevel(props: ResolveRelVerticalDatumProps): LevelInches {
    if (!isResolveWallVerticalDatumProps(props)) {
      throw new Error("Invalid props type for LegacyWallVerticalDatumAbstraction");
    }

    const { wallSide, extraOffset } = props;

    return wallSideDatumToLevelOffset(this.getDatumEntity(), wallSide, extraOffset);
  }

  isValidForLevel(_levelId: string | undefined): boolean {
    return false;
  }
}

export class LegacyWallHorizontalDatumAbstraction extends LegacyWallHorizontalDatumAbstractionBase {
  constructor(datum: WallHorizontalDatumEntity) {
    super(datum);
  }

  isValidForUsage<T extends DatumUsageType>(usageType: T): this is (typeof datumUsageTypeToDatumAbstractionType)[T] {
    return usageType === DatumHorizontalUsageType.WALL_HORIZONTAL;
  }

  resolveHorizontalDatum(datumRefLen: number, extraOffset?: number): number {
    return evaluateHorizontalDatumOffset(this.datum, datumRefLen, extraOffset);
  }
}

export class WallPlaneHorizontalDatumAbstraction extends WallPlaneHorizontalDatumAbstractionBase {
  constructor(datum: WallPlaneHorizontalDatumEntity) {
    super(datum);
  }

  isValidForUsage<T extends DatumUsageType>(usageType: T): this is (typeof datumUsageTypeToDatumAbstractionType)[T] {
    return usageType === DatumHorizontalUsageType.WALL_PLANE_HORIZONTAL;
  }

  resolveWallPlaneU(): number {
    return this.datum.offset ?? 0;
  }

  getPlaneHash(): string {
    return this.datum.planeHash;
  }
}

/**
 * DatumAbstraction implementation for legacy foundation vertical datums
 */
export class LegacyFoundationVerticalDatumAbstraction extends LegacyFoundationVerticalDatumAbstractionBase {
  constructor(datum: LegacyFoundationVerticalDatumEntity) {
    super(datum);
  }

  isValidForUsage<T extends DatumUsageType>(usageType: T): this is (typeof datumUsageTypeToDatumAbstractionType)[T] {
    return usageType === DatumVerticalUsageType.FOUNDATION_VERTICAL;
  }

  isBaseVerticalDatumType(_isBottomDatum: boolean, _extraOffset?: number): boolean {
    return false;
  }

  isRoofDatum(): boolean {
    return false;
  }

  hasCompatibleAnchor(anchor: FoundationDatumVerticalAnchor): boolean {
    return this.datum.anchor === anchor;
  }

  resolveVerticalDatum(props: ResolveAbsVerticalDatumProps): WorldInches {
    if (!isResolveFoundationVerticalDatumProps(props)) {
      throw new Error("Invalid props type for LegacyFoundationVerticalDatumAbstraction");
    }

    const { level, path, extraOffset } = props;

    // after isResolveFoundationVerticalDatumProps check,
    // path.datums is guaranteed to be defined, so we can assert the type here
    return evaluateLegacyFoundationVerticalDatumOffset({
      datum: this.getDatumEntity(),
      level,
      path: path,
      extraOffset,
    });
  }

  resolveVerticalDatumRelativeToLevel(props: ResolveRelVerticalDatumProps): LevelInches {
    if (!isResolveFoundationVerticalDatumProps(props)) {
      throw new Error("Invalid props type for LegacyFoundationVerticalDatumAbstraction");
    }

    return worldInchesToLevel(this.resolveVerticalDatum(props), props.level.floorElevation);
  }

  isValidForLevel(_levelId: string | undefined): boolean {
    return false;
  }
}

/**
 * DatumAbstraction implementation for legacy foundation lateral datums
 */
export class LegacyFoundationLateralDatumAbstraction extends LegacyFoundationLateralDatumAbstractionBase {
  constructor(datum: LegacyFoundationLateralDatumEntity) {
    super(datum);
  }

  isValidForUsage<T extends DatumUsageType>(usageType: T): this is (typeof datumUsageTypeToDatumAbstractionType)[T] {
    return usageType === DatumLateralUsageType.FOUNDATION_LATERAL;
  }

  hasCompatibleAnchor(anchor: WallLateralDatumAnchor): boolean {
    return this.datum.anchor === anchor;
  }

  resolveLateralDatum(): number {
    return this.datum.offset ?? 0;
  }

  resolveWallLayer(): WallLayerType | undefined {
    return wallLateralDatumAnchorToWallLayer[this.datum.anchor];
  }
}

/**
 * DatumAbstraction implementation for legacy level datums
 */
export class LegacyLevelDatumAbstraction extends LegacyLevelDatumAbstractionBase {
  constructor(datum: LegacyLevelDatumEntity) {
    super(datum);
  }

  isValidForUsage<T extends DatumUsageType>(usageType: T): this is (typeof datumUsageTypeToDatumAbstractionType)[T] {
    return (
      usageType === DatumVerticalUsageType.LEVEL ||
      usageType === DatumVerticalUsageType.CEILING ||
      usageType === DatumVerticalUsageType.WALL_VERTICAL
    );
  }

  isBaseVerticalDatumType(_isBottomDatum: boolean, _extraOffset?: number): boolean {
    return false;
  }

  isRoofDatum(): boolean {
    return false;
  }

  hasCompatibleAnchor(_anchor: WallVerticalDatumAnchor): boolean {
    return false;
  }

  resolveVerticalDatum(props: ResolveAbsVerticalDatumProps): WorldInches {
    if (!isResolveLevelVerticalDatumProps(props)) {
      throw new Error("Invalid props type for LegacyLevelDatumAbstraction");
    }

    const { level, levelAttributes, ignoreAnchor, extraOffset } = props;

    return evaluateLegacyLevelDatumOffset({
      level,
      levelAttributes,
      datum: this.getDatumEntity(),
      ignoreAnchor,
      extraOffset,
    });
  }

  resolveVerticalDatumRelativeToLevel(props: ResolveRelVerticalDatumProps): LevelInches {
    if (!isResolveLevelVerticalDatumProps(props)) {
      throw new Error("Invalid props type for LegacyLevelDatumAbstraction");
    }

    return worldInchesToLevel(this.resolveVerticalDatum(props), props.level.floorElevation);
  }

  isValidForLevel(levelId: string | undefined): boolean {
    if (!levelId) {
      return false;
    }

    return this.datum.levelId === levelId;
  }
}

/**
 * DatumAbstraction implementation for stacked level datums
 */
export class StackedLevelDatumAbstraction extends StackedLevelDatumAbstractionBase {
  private static lastStackedLevelTopOfWallTable: { [levelId: string]: WorldInches } = {};

  constructor(datum: StackedLevelDatumEntity) {
    super(datum);
  }

  static resetLastStackedLevelTopOfWall() {
    this.lastStackedLevelTopOfWallTable = {};
  }

  static setLastStackedLevelTopOfWall(levelId: string, topOfWall: WorldInches) {
    this.lastStackedLevelTopOfWallTable[levelId] = topOfWall;
  }

  isValidForUsage<T extends DatumUsageType>(usageType: T): this is (typeof datumUsageTypeToDatumAbstractionType)[T] {
    return usageType === DatumVerticalUsageType.LEVEL_PLACEMENT;
  }

  isBaseVerticalDatumType(_isBottomDatum: boolean, _extraOffset?: number): boolean {
    return false;
  }

  isRoofDatum(): boolean {
    return false;
  }

  hasCompatibleAnchor(_anchor: WallVerticalDatumAnchor): boolean {
    return false;
  }

  resolveVerticalDatum(props: ResolveAbsStackedLevelDatumProps): WorldInches {
    const { stackedAboveLevelId, currentLevelId, extraOffset = 0 } = props;

    return worldInchesNew(
      (StackedLevelDatumAbstraction.lastStackedLevelTopOfWallTable[stackedAboveLevelId ?? currentLevelId] ?? 0) +
        (extraOffset ?? 0),
    );
  }

  resolveVerticalDatumRelativeToLevel(props: ResolveRelStackedLevelDatumProps): LevelInches {
    return worldInchesToLevel(this.resolveVerticalDatum(props), props.level.floorElevation);
  }

  isValidForLevel(_levelId: string | undefined): boolean {
    return false;
  }
}

/**
 * datumUsage: The datum usage to get the abstraction for. This is the property living on the enity referencing the datum.
 * datumAbstractions: The table of datum abstractions to search through.
 * requestedUsageType: The datum usage type that the caller is requesting.
 *
 * Returns the datum abstraction that is valid for the desired usage, or undefined if no valid abstraction is found for the desired usage.
 */
interface GetDatumAbstractionOptions<T extends DatumUsageType, Q extends DatumUsageType> {
  readonly datumUsage: DatumUsage<T> | undefined;
  readonly datumAbstractions: Table<DatumAbstraction>;
  readonly requestedUsageType: Q;
  readonly componentInstanceChain?: string[];
}

export function getDatumAbstraction<T extends DatumUsageType, Q extends DatumUsageType>({
  datumUsage,
  datumAbstractions,
  requestedUsageType,
  componentInstanceChain,
}: GetDatumAbstractionOptions<T, Q>): (typeof datumUsageTypeToDatumAbstractionType)[Q] | undefined {
  const datumAbstraction = resolveInstanceScopedEntity({
    path: datumUsage,
    table: datumAbstractions,
    fallbackComponentInstanceChain: componentInstanceChain,
  });

  if (datumAbstraction?.isValidForUsage(requestedUsageType)) {
    return datumAbstraction;
  }

  return undefined;
}

/**
 * datumUsage: The datum usage to get the abstraction for. This is the property living on the enity referencing the datum.
 * datumAbstractions: The table of datum abstractions to search through.
 * requestedUsageType: The datum usage type that the caller is requesting.
 * fallbackDatumAbstraction: The datum abstraction to return if no valid abstraction is found for the desired usage.
 *
 * Returns the datum abstraction that is valid for the desired usage, or the fallback datum abstraction if no valid abstraction is found for the desired usage.
 */
interface GetDatumAbstractionWithFallbackOptions<T extends DatumUsageType, Q extends DatumUsageType> {
  readonly datumUsage: DatumUsage<T> | undefined;
  readonly datumAbstractions: Table<DatumAbstraction>;
  readonly requestedUsageType: Q;
  readonly fallbackDatumAbstraction: (typeof datumUsageTypeToDatumAbstractionType)[Q];
  readonly componentInstanceChain?: string[];
}

export function getDatumAbstractionWithFallback<T extends DatumUsageType, Q extends DatumUsageType>({
  datumUsage,
  datumAbstractions,
  requestedUsageType,
  fallbackDatumAbstraction,
  componentInstanceChain,
}: GetDatumAbstractionWithFallbackOptions<T, Q>): (typeof datumUsageTypeToDatumAbstractionType)[Q] {
  return (
    getDatumAbstraction({
      datumUsage,
      datumAbstractions,
      requestedUsageType,
      componentInstanceChain,
    }) ?? fallbackDatumAbstraction
  );
}

function wallSideDatumToLevelOffset(
  datum: LegacyWallVerticalDatumEntity,
  wallSide: Pick<WallSideDescriptor, "wallSideDatums">,
  datumOffset: number = 0,
): LevelInches {
  const { anchor } = datum;
  const { wallSideDatums } = wallSide;

  switch (anchor) {
    case WallVerticalDatumAnchor.BASE_WALL: {
      /**
       * @TODO - BASE_WALL should really be level.floorElevation to level.topOfWall for a consistent alignment
       * across a level.
       *
       * This setup will maintain current baselines on the existing houses,
       * by simulating the behavior that used to be attributed to the face generated at this wall side.
       * In legacy homes, the bottom of the face that was generated represented the max of floor, foundation and level,
       * hence the 0 here.
       *
       * We should consider a migration into LEGACY_BASE_WALL and not allow this behavior any longer.
       */
      const datumBottom = levelInchesNew(Math.max(wallSideDatums.floorTop, wallSideDatums.foundationTop, 0));
      const datumTop = levelInchesNew(wallSideDatums.ceilingBottom);
      return evaluateVerticalDatumOffset(datum, datumBottom, datumTop, datumOffset);
    }
    case WallVerticalDatumAnchor.GABLE_END: {
      /**
       * @TODO - find a way of attaching gable end datums to wall side datums. There
       * currently is no way of knowing what the gable end offset is relative to a wall side.
       *
       * Facades on walls with a GABLE_END datum currently always return a datum of ceilingTop.
       * In the event that this justification is BOTOM_UP - this is correct. However in the event that this
       * justification is TOP_DOWN, we do not have enough information to produce an accurate datum
       * from the top of the gable end. This needs to be tackled in a dedicated body of work.
       */
      // to avoid regressions
      if (wallSideDatums.roofBottom === wallSideDatums.roofTop) {
        return wallSideDatums.roofBottom;
      }

      const datumBottom = levelInchesNew(wallSideDatums.roofBottom);
      const datumTop = levelInchesNew(wallSideDatums.roofTop);
      return evaluateVerticalDatumOffset(datum, datumBottom, datumTop, datumOffset);
    }
    default: {
      assertUnreachable(anchor as never);
    }
  }
}

interface EvaluateLegacyLevelDatumOffsetOptions {
  readonly level: Level;
  readonly datum: LegacyLevelDatumEntity;
  readonly levelAttributes: LevelAttributes;
  readonly ignoreAnchor?: boolean;
  readonly extraOffset?: number;
}

function evaluateLegacyLevelDatumOffset({
  level,
  levelAttributes,
  datum,
  ignoreAnchor = false,
  extraOffset = 0,
}: EvaluateLegacyLevelDatumOffsetOptions): WorldInches {
  let absoluteHeight = level.floorElevation + (datum.offset ?? 0) + extraOffset;

  // legacy behavior from generateResolvedLevelDatums
  if (ignoreAnchor) {
    return worldInchesNew(absoluteHeight);
  }

  switch (datum.anchor) {
    case LevelDatumAnchor.LEVEL_BOTTOM_OF_FLOOR_PLATE: {
      absoluteHeight -= level.floorPlateThickness;
      break;
    }
    case LevelDatumAnchor.LEVEL_FLOOR_ELEVATION:
      break;
    case LevelDatumAnchor.LEVEL_WINDOW_HEAD_HEIGHT: {
      absoluteHeight += levelAttributes.windowHeadHeight;
      break;
    }
    case LevelDatumAnchor.LEVEL_TOP_OF_WALL: {
      absoluteHeight += level.topOfWall;
      break;
    }
    case LevelDatumAnchor.LEVEL_GRADE: {
      absoluteHeight += level.levelDatumsGenerated.grade;
      break;
    }
  }

  return worldInchesNew(absoluteHeight);
}

interface EvaluateLegacyFoundationVerticalDatumOffsetOptions {
  readonly level: Level;
  readonly datum: LegacyFoundationVerticalDatumEntity;
  readonly path: Pick<FoundationPath, "datums">;
  readonly extraOffset?: number;
}

function evaluateLegacyFoundationVerticalDatumOffset({
  datum,
  level,
  path,
  extraOffset = 0,
}: EvaluateLegacyFoundationVerticalDatumOffsetOptions): WorldInches {
  const { floorElevation, topOfWall, levelDatumsGenerated } = level;
  const { foundation } = levelDatumsGenerated;
  const { datums } = path;
  const { floorPlateBottom, slabTop, slabBottom } = datums ?? {
    floorPlateBottom: worldInchesNew(floorElevation),
    slabTop: worldInchesNew(floorElevation),
    slabBottom: worldInchesNew(floorElevation),
  };
  const { anchor, offset = 0 } = datum;

  let worldInches = levelInchesToWorld(levelInchesNew(0), floorElevation);

  switch (anchor) {
    /**
     * @deprecated - should be replaced with LevelDatumAnchor.LEVEL_FLOOR_ELEVATION
     * Datum usage should be replaced with LegacyLevelDatumEntity
     * and this anchor should be deleted
     */
    case FoundationDatumVerticalAnchor.LEVEL_FLOOR_ELEVATION: {
      worldInches = levelInchesToWorld(levelInchesNew(0), floorElevation);
      break;
    }
    /**
     * @deprecated - should be replaced with LevelDatumAnchor.LEVEL_TOP_OF_WALL
     * Datum usage should be replaced with LegacyLevelDatumEntity
     * and this anchor should be deleted
     */
    case FoundationDatumVerticalAnchor.LEVEL_TOP_OF_WALL: {
      worldInches = levelInchesToWorld(levelInchesNew(topOfWall), floorElevation);
      break;
    }

    // --- BELOW THIS LINE ARE FOUNDATION SPECIFIC ANCHORS ---

    case FoundationDatumVerticalAnchor.FOUNDATION: {
      worldInches = levelInchesToWorld(levelInchesNew(foundation), floorElevation);
      break;
    }
    case FoundationDatumVerticalAnchor.BOTTOM_OF_FLOORPLATE: {
      worldInches = floorPlateBottom;
      break;
    }
    case FoundationDatumVerticalAnchor.TOP_OF_SLAB: {
      worldInches = slabTop;
      break;
    }
    case FoundationDatumVerticalAnchor.BOTTOM_OF_SLAB: {
      worldInches = slabBottom;
      break;
    }
    default: {
      assertUnreachable(anchor);
    }
  }

  return worldInchesNew(worldInches + offset + extraOffset);
}

function evaluateVerticalDatumOffset<T extends NominalNumber>(
  datum: Pick<WallAssemblyVerticalDatum | FacadeVerticalDatum, "justification" | "offset" | "offsetNormalized">,
  datumBottom: T,
  datumTop: T,
  datumOffset: number = 0,
) {
  const { offsetNormalized, offset = 0, justification = VerticalJustification.BOTTOM_UP } = datum;

  const datumHeight = datumTop - datumBottom;
  const offsetAdjusted = offsetNormalized ? datumHeight * offset : offset;

  let evaluatedOffset = 0;

  switch (justification) {
    case VerticalJustification.BOTTOM_UP: {
      evaluatedOffset = datumBottom + offsetAdjusted + datumOffset;
      break;
    }
    case VerticalJustification.TOP_DOWN: {
      evaluatedOffset = datumTop - offsetAdjusted + datumOffset;
      break;
    }
    default:
  }
  return nominalNumberNew<T>(evaluatedOffset);
}

/**
 * @TODO - create NominalNumberTypes for Vertical/Horizontal/Lateral Numbers
 */
function evaluateHorizontalDatumOffset(
  datum: Pick<WallHorizontalDatumEntity, "justification" | "offset" | "offsetNormalized">,
  datumRefLen: number,
  datumOffset = 0,
) {
  const { offsetNormalized, offset = 0, justification } = datum;

  const offsetAdjusted = offsetNormalized ? datumRefLen * offset : offset;

  let evaluatedOffset = 0;

  switch (justification) {
    case HorizontalJustification.START_TO_END: {
      evaluatedOffset = offsetAdjusted + datumOffset;
      break;
    }
    case HorizontalJustification.END_TO_START: {
      evaluatedOffset = datumRefLen - offsetAdjusted + datumOffset;
      break;
    }
  }
  return evaluatedOffset;
}
